import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'

import { PageHeader, SearchInput, combineClassnames } from 'app/common/presentation'
import { EventsIcon, FilterIcon, PauseIcon, PlayIcon } from 'app/common/presentation/icons'

import { Code, DropDown, IconButton, Switch } from 'components'

import { Event, EventMessage, mapEventName } from '../types'
import { EventType } from '../types/event'
import styles from './Events.module.scss'
import useWebSocket from './useWebSockets'

const maxEventsPerType = 50
const timestampFormat = 'MMM dd, HH:mm aa'
const eventFilterTypes: EventType[] = ['track', 'page', 'identify']

const reduceToMaxEventsPerType = (events: Event[]) => {
  const numberOfEventsPerType: Partial<Record<EventType, number>> = {}

  return events.filter((event) => {
    const numberOfEventsForType = numberOfEventsPerType[event.type]
    if (numberOfEventsForType) {
      if (numberOfEventsForType < maxEventsPerType) {
        numberOfEventsPerType[event.type] = numberOfEventsForType + 1

        return true
      }

      return false
    }

    numberOfEventsPerType[event.type] = 1
    return true
  })
}

const Events = () => {
  const { t } = useTranslation('events')
  const [liveEvents, setLiveEvents] = useState<Event[]>()
  const [pausedEvents, setPausedEvents] = useState<Event[]>()
  const [filteredEvents, setFilteredEvents] = useState<Event[]>()
  const [paused, setPaused] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [selectedFilterTypes, setSelectedFilterTypes] = useState(eventFilterTypes)
  const [eventDetails, setEventDetails] = useState<Event | null>(null)

  const handleMessage = useCallback((message: string) => {
    const eventMessage: EventMessage = JSON.parse(message) as EventMessage

    switch (eventMessage.type) {
      case 'existing_events':
        setLiveEvents(reduceToMaxEventsPerType(eventMessage.events.reverse()))
        break
      case 'new_event':
        setLiveEvents((prev) => {
          const updated = prev ? [eventMessage.event, ...prev] : [eventMessage.event]
          return reduceToMaxEventsPerType(updated)
        })
        break
    }
  }, [])

  useEffect(() => {
    if (!paused) {
      setPausedEvents(liveEvents)
    }
  }, [liveEvents, paused])

  useEffect(() => {
    if (pausedEvents) {
      setFilteredEvents(
        pausedEvents.filter(
          (event) =>
            selectedFilterTypes.includes(event.type) &&
            JSON.stringify(event, null, 2).includes(searchInput),
        ),
      )
    }
  }, [pausedEvents, searchInput, selectedFilterTypes])

  useEffect(() => {
    // reset details, if event flows out of (not paused) event list
    if (pausedEvents && !pausedEvents.some((event) => event.id === eventDetails?.id)) {
      setEventDetails(null)
    }
  }, [eventDetails, pausedEvents, searchInput])

  useWebSocket('/events', handleMessage)

  return (
    <>
      <PageHeader icon={EventsIcon} title={t('title')} />
      <div className={styles.eventsContainer}>
        <div>
          <div className={styles.menu}>
            <Switch
              className={styles.playPause}
              checked={paused}
              iconChecked={PauseIcon}
              iconUnchecked={PlayIcon}
              labelChecked={t('pause')}
              labelUnchecked={t('live')}
              name="connections-type"
              onChange={() => setPaused(!paused)}
            />
            <div className={styles.searchFilter}>
              <SearchInput
                onChange={setSearchInput}
                placeholder={t('search')}
                value={searchInput}
              />

              <DropDown
                classNameMenu={styles.filterMenu}
                data={eventFilterTypes}
                onReset={() => setSelectedFilterTypes(eventFilterTypes)}
                onSelect={(type) =>
                  setSelectedFilterTypes((prev) =>
                    prev.includes(type)
                      ? prev.filter((selectedType) => selectedType !== type)
                      : [...prev, type],
                  )
                }
                renderValueContent={(type) => t(`types.${type}`)}
                selection={selectedFilterTypes}
                title={t('filter')}
                type="multiple"
              >
                <IconButton
                  active={selectedFilterTypes.length !== eventFilterTypes.length}
                  icon={FilterIcon}
                  variant="secondary"
                />
              </DropDown>
            </div>
          </div>
          <div className={styles.events}>
            <div className={styles.header}>
              <div>{t('type')}</div>
              <div>{t('name')}</div>
              <div>{t('date')}</div>
            </div>
            {filteredEvents && (
              <AnimatePresence initial={false}>
                {filteredEvents.map((event) => (
                  <motion.div
                    layout
                    animate={{ opacity: 1, y: 0 }}
                    className={combineClassnames(
                      styles.row,
                      event.id === eventDetails?.id ? styles.selected : undefined,
                    )}
                    initial={{ opacity: 1, y: -46 }}
                    key={event.id}
                    onClick={() => setEventDetails(event)}
                  >
                    <div>
                      <div className={styles.type}>{event.type}</div>
                    </div>
                    <div>{mapEventName(event)}</div>
                    <div>{format(new Date(event.timestamp), timestampFormat)}</div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>
        <div className={styles.details}>
          {eventDetails && (
            <>
              <div className={styles.header}>
                <div className={styles.event}>
                  <div className={styles.name}>{mapEventName(eventDetails)}</div>
                  <div className={styles.type}>{eventDetails.type}</div>
                </div>
                <div className={styles.timestamp}>
                  {format(new Date(eventDetails.timestamp), timestampFormat)}
                </div>
              </div>
              <div className={styles.code}>
                <Code
                  code={eventDetails ? JSON.stringify(eventDetails, null, 2) : ''}
                  showCopy
                  what={'Event payload'}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Events
